import {
	Grid,
	MenuItem,
	Paper,
	Snackbar,
	Typography,
	makeStyles,
} from "@material-ui/core";
import AddBoxIcon from "@material-ui/icons/AddBox";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FindInPageIcon from "@material-ui/icons/FindInPage";
import { Alert } from "@material-ui/lab";
import { Tree } from "@minoru/react-dnd-treeview";
import {
	DragLayerMonitorProps,
	NodeModel,
} from "@minoru/react-dnd-treeview/dist/types";
import React, {
	type SyntheticEvent,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { useAssetContext } from "../../../../../context/assetContext";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { useConfigurationAssetContext } from "../../context/ConfigurationAssetContext";
import { useSearchAssets } from "../../hooks/useSearchAssets";
import { FileProperties } from "../../models/FileProperties";
import { CreateNewAssetDialog } from "../dialogs/create-new-asset-dialog";
import { EditAssetDialog } from "../dialogs/edit-asset-dialog";
import { RemoveAssetDialog } from "../dialogs/remove-asset-dialog";
import { ViewerAssetDialog } from "../dialogs/viewer-asset-dialog";
import { useAssetActions } from "../hooks/useAssetActions";
import { CustomDragPreview } from "./CustomDragPreview";
import { CustomNode } from "./CustomNode";
import { MultipleDragPreview } from "./MultipleDragPreview";

const useStyles = makeStyles((theme) => ({
	root: {},
	contextMenu: {
		backgroundColor: "#F8F8F8",
		borderRadius: "4px",
		padding: "0",
		height: "auto",
		width: "170px",
		margin: "0",
		position: "fixed",
		listStyle: "none",
		boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
		zIndex: 1000,
	},
	menuItem: {
		padding: "6px 12px 6px 10px",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		backgroundColor: "#F8F8F8",
		fontSize: "12px",
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
		"&:last-child": {
			borderBottom: "none",
		},
		"&:hover": {
			backgroundColor: "#d4edff",
		},
	},
	storyRoot: {
		display: "grid",
		height: "100%",
		gridTemplateRows: "auto 1fr",
		listStyleType: "none !important",
	},

	assetRoot: {
		boxSizing: "border-box",
		height: "100%",
		padding: "32px",
	},

	dropTarget: {
		boxShadow: "0 0 0 1px #1967d2 inset",
	},
}));

export const AssetViewComponent = () => {
	const [selectedNodes, setSelectedNodes] = useState<
		NodeModel<FileProperties>[]
	>([]);
	const classes = useStyles();
	const [isDragging, setIsDragging] = useState(false);
	const [isCtrlPressing, setIsCtrlPressing] = useState(false);
	const [isDoubleClick, setIsDoubleClick] = useState(false);
	const {
		state: {
			allAssetNodes,
			openAssets,
			showContextMenu,
			showCreateNewContextMenu,
			assetNodeSelectedInTree,
			anchorPoint: { x, y },
		},
		actions: {
			setShowContextMenu,
			setShowCreateNewContextMenu,
			setMenuContextAssetId,
			setAnchorPointX,
			setAnchorPointY,
			setAssetNodeSelectedInTree,
		},
	} = useConfigurationAssetContext();

	const {
		state: { allAssets },
	} = useAssetContext();

	const [showCreateNewAssetDialog, setShowCreateNewAssetDialog] =
		useState<boolean>(false);
	const [showViewerAssetDialog, setShowViewerAssetDialog] =
		useState<boolean>(false);
	const [showRemoveAssetDialog, setShowRemoveAssetDialog] =
		useState<boolean>(false);
	const [showEditAssetDialog, setShowEditAssetDialog] =
		useState<boolean>(false);

	const [errorUpdate, setErrorUpdate] = useState<string>("");

	const [searchError, setSearchError] = useState<string>("");

	const [isLoading, setIsLoading] = useState(false);
	const [updatedSuccessfullyMessage, setUpdatedSuccessfullyMessage] =
		useState(false);

	const { searchAssets } = useSearchAssets({
		setSearchError,
		setAllAssetLoading: setIsLoading,
	});

	const { handleDrop, handleCtrlClick, handleDragStart, handleDragEnd } =
		useAssetActions({
			asset: allAssetNodes,
			selectedNodes,
			setSelectedNodes,
			setIsDragging,
			isCtrlPressing,
			setIsCtrlPressing,
			onAssetUpdateStart: () => {
				setIsLoading(true);
			},
			onAssetUpdateEnd: (result) => {
				if (result.ok) {
					setUpdatedSuccessfullyMessage(true);
					searchAssets();
				} else {
					setErrorUpdate(result.message);
				}
			},
		});

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setUpdatedSuccessfullyMessage(false);
	};

	const handleClick = (e: any) => {
		if (showContextMenu) {
			setShowContextMenu(false);
			setMenuContextAssetId(undefined);
		}
		if (showCreateNewContextMenu) {
			setShowCreateNewContextMenu(false);
			setMenuContextAssetId(undefined);
		}
	};

	const handleCreateNewContextMenu = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowContextMenu(false);
		const top = e.clientY;
		const left = e.clientX;

		setAnchorPointX(left);
		setAnchorPointY(top);
		setShowCreateNewContextMenu(true);
		setAssetNodeSelectedInTree(undefined);
	};

	const handleContextMenu = (
		data: NodeModel<FileProperties>,
		e: React.MouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		setShowCreateNewContextMenu(false);
		const top = e.clientY;
		const left = e.clientX;

		setAnchorPointX(left);
		setAnchorPointY(top);

		setSelectedNodes([data]);

		setShowContextMenu(true);
		const selectedNode = allAssets.find(
			(node: any) => node.AssetId === data.id,
		);
		if (selectedNode) {
			setAssetNodeSelectedInTree(selectedNode);
		}
	};

	const handleShowViewerAsset = (
		data: NodeModel<FileProperties>,
		e: React.MouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		setIsDoubleClick(true);
		const selectedNode = allAssets.find(
			(node: any) => node.AssetId === data.id,
		);
		if (selectedNode) {
			setAssetNodeSelectedInTree(selectedNode);
			setShowViewerAssetDialog(true);
		}
	};

	const handleOnHideViewer = () => {
		setShowViewerAssetDialog(false);
		setIsDoubleClick(false);
		setShowCreateNewContextMenu(false);
		setShowContextMenu(false);
	};

	useEffect(() => {
		searchAssets();
	}, []);

	const openAssetsArray = useMemo(() => {
		return Object.keys(openAssets).map((el) => Number.parseInt(el));
	}, [openAssets]);

	return (
		<div style={{ width: "100%", height: "100%" }}>
			<div onClick={handleClick}>
				<div onContextMenu={handleCreateNewContextMenu}>
					<div>
						<Grid
							container
							justifyContent="center"
							alignItems="center"
							className={classes.root}
						>
							<div style={{ width: "100%", padding: "15px 20px 25px" }}>
								<Typography
									variant="h5"
									style={{ margin: "10px 0 6px", fontWeight: 600 }}
								>
									Assets Tree
								</Typography>
								{isLoading ? (
									<Grid container style={{ width: "100%", height: "100%" }}>
										<Grid
											item
											xs={12}
											md={12}
											style={{
												height: "66vh",
												justifyContent: "center",
												alignItems: "center",
											}}
										>
											<div
												style={{
													width: "100%",
													height: "100%",
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												<LazyLoading />
											</div>
										</Grid>
									</Grid>
								) : (
									<Paper
										elevation={2}
										style={{
											width: "100%",
											padding: "15px 20px 10px",
											borderRadius: "0.75rem",
										}}
									>
										<Grid container alignItems="center">
											<Grid
												container
												style={{
													height: "66vh",
													overflowY: "auto",
													paddingBottom: 10,
												}}
											>
												<Grid>
													<Tree
														rootId={0}
														initialOpen={openAssetsArray}
														tree={allAssetNodes}
														enableAnimateExpand={true}
														classes={{
															root: classes.assetRoot,
															dropTarget: classes.dropTarget,
														}}
														onDrop={handleDrop}
														onDragStart={handleDragStart}
														onDragEnd={handleDragEnd}
														canDrop={(asset: any, options: any): boolean => {
															return !selectedNodes.some(
																(selectedNode) =>
																	selectedNode.id === options.dropTargetId,
															);
														}}
														render={(node, options) => {
															const selected = selectedNodes.some(
																(selectedNode) => selectedNode.id === node.id,
															);

															return (
																<CustomNode
																	setContextMenuOver={handleContextMenu}
																	node={node}
																	{...options}
																	isSelected={selected}
																	isDragging={selected && isDragging}
																	onClick={handleCtrlClick}
																	onDoubleClick={handleShowViewerAsset}
																/>
															);
														}}
														dragPreviewRender={(
															monitorProps: DragLayerMonitorProps<FileProperties>,
														) => {
															if (selectedNodes.length > 1) {
																return (
																	<MultipleDragPreview
																		dragSources={selectedNodes}
																	/>
																);
															}

															return (
																<CustomDragPreview
																	monitorProps={monitorProps}
																/>
															);
														}}
													/>
												</Grid>
											</Grid>
										</Grid>
									</Paper>
								)}
								<CreateNewAssetDialog
									show={showCreateNewAssetDialog}
									onHide={(shouldUpdate) => {
										setShowCreateNewAssetDialog(false);
										if (shouldUpdate) {
											searchAssets();
										}
									}}
								/>
								<EditAssetDialog
									show={showEditAssetDialog}
									onHide={(shouldUpdate) => {
										setShowEditAssetDialog(false);
										if (shouldUpdate) {
											searchAssets();
										}
									}}
								/>
								<ViewerAssetDialog
									show={showViewerAssetDialog}
									onHide={() => handleOnHideViewer()}
								/>
								<RemoveAssetDialog
									show={showRemoveAssetDialog}
									onHide={(shouldUpdate) => {
										setShowRemoveAssetDialog(false);
										if (shouldUpdate) {
											searchAssets();
										}
									}}
								/>
							</div>
						</Grid>
						<Snackbar
							open={updatedSuccessfullyMessage}
							autoHideDuration={2500}
							onClose={handleClose}
						>
							<Alert severity="success" onClose={handleClose}>
								Asset updated successfully
							</Alert>
						</Snackbar>
					</div>
					{showContextMenu && !isDoubleClick ? (
						<ul
							className={classes.contextMenu}
							style={{ top: y, left: x, padding: "6px 0" }}
						>
							<MenuItem
								className={classes.menuItem}
								onClick={() => setShowViewerAssetDialog(true)}
							>
								<Grid container style={{ alignItems: "center" }}>
									<Grid item md={2} xs={3}>
										<FindInPageIcon style={{ width: 18 }} />
									</Grid>
									<Grid item md={9} xs={9}>
										<div style={{ marginLeft: 5 }}>View Asset</div>
									</Grid>
								</Grid>
							</MenuItem>
							<MenuItem
								className={classes.menuItem}
								onClick={() => setShowEditAssetDialog(true)}
							>
								<Grid container style={{ alignItems: "center" }}>
									<Grid item md={2} xs={3}>
										<EditIcon style={{ width: 18 }} />
									</Grid>
									<Grid item md={9} xs={9}>
										<div style={{ marginLeft: 5 }}>Edit Asset</div>
									</Grid>
								</Grid>
							</MenuItem>
							<MenuItem
								className={classes.menuItem}
								onClick={() => setShowCreateNewAssetDialog(true)}
							>
								<Grid container style={{ alignItems: "center" }}>
									<Grid item md={2} xs={3}>
										<AddBoxIcon style={{ width: 18 }} />
									</Grid>
									<Grid item md={9} xs={9}>
										<div style={{ marginLeft: 5 }}>Create Asset</div>
									</Grid>
								</Grid>
							</MenuItem>
							{assetNodeSelectedInTree?.IsBranch === false && (
								<MenuItem
									className={classes.menuItem}
									onClick={() => setShowRemoveAssetDialog(true)}
								>
									<Grid container style={{ alignItems: "center" }}>
										<Grid item md={2} xs={3}>
											<DeleteIcon style={{ width: 18 }} />
										</Grid>
										<Grid item md={9} xs={9}>
											<div style={{ marginLeft: 5 }}>Remove Asset</div>
										</Grid>
									</Grid>
								</MenuItem>
							)}
						</ul>
					) : (
						""
					)}
					{showCreateNewContextMenu ? (
						<ul className={classes.contextMenu} style={{ top: y, left: x }}>
							<MenuItem
								className={classes.menuItem}
								onClick={() => setShowCreateNewAssetDialog(true)}
							>
								<Grid container style={{ alignItems: "center" }}>
									<Grid item md={2} xs={3}>
										<AddBoxIcon style={{ width: 18 }} />
									</Grid>
									<Grid item md={9} xs={9}>
										<div style={{ marginLeft: 5 }}>Create Asset</div>
									</Grid>
								</Grid>
							</MenuItem>
						</ul>
					) : (
						""
					)}
				</div>
			</div>
			<ErrorModal error={errorUpdate} onHide={() => setErrorUpdate("")} />

			<ErrorModal error={searchError} onHide={() => setSearchError("")} />
		</div>
	);
};
